import Shepherd from 'shepherd.js';
import { TOUR_BUTTONS } from '../shepherd-defaults';
import {
  dismissCurrentShepherdStep,
  ensureShepherdModalOverlayHidden,
  exitShepherdLocationTypingFreeze,
  registerInteractiveTourGuards,
} from '../shepherd-interactive-step';
import {
  registerTourHighlightedFieldLockGuards,
  releaseAllShepherdTourFieldLocks,
  SHEPHERD_TOUR_READONLY_HIGHLIGHT_STEP_OPTIONS,
  withTourFieldLockTipMessage,
} from '../shepherd-tour-field-lock';
import {
  resetMessagesConnectionsSearchTargetPadding,
  resolveMessagesConnectionsSearchTarget,
  setMessagesConnectionsSearchInteractionActive,
} from '../messages-connections-search.shepherd';
import { waitForElement } from '../shepherd.utils';

type ShepherdTour = InstanceType<typeof Shepherd.Tour>;

export const MESSAGES_CONNECTIONS_TOUR = 'messages-connections';

export const MESSAGES_CONNECTIONS_INTRO_STEP_ID = 'messages-connections-intro';

export const MESSAGES_CONNECTIONS_SEARCH_LIST_STEP_ID = 'messages-connections-search-with-list';

/** Steps that highlight the connections search bar (typing + user search list). */
export const MESSAGES_CONNECTIONS_SEARCH_STEP_IDS = [
  MESSAGES_CONNECTIONS_INTRO_STEP_ID,
  MESSAGES_CONNECTIONS_SEARCH_LIST_STEP_ID,
] as const;

/** True while Connections tab still shows skeleton rows. */
export function isMessagesConnectionsSkeletonVisible(): boolean {
  return !!document.querySelector('#connections-pane .message-item.skeleton-loader');
}

export function messagesConnectionsHasEmptyState(): boolean {
  return !!document.querySelector('[data-tour="messages-connections-empty-state"]');
}

export function messagesConnectionsHasList(): boolean {
  return !!document.querySelector(
    '#connections-pane .messages-list .message-item:not(.skeleton-loader)',
  );
}

function clearMessagesConnectionsSearchStepOverlay(): void {
  exitShepherdLocationTypingFreeze(resolveMessagesConnectionsSearchTarget);
  setMessagesConnectionsSearchInteractionActive(false);
  resetMessagesConnectionsSearchTargetPadding();
  releaseAllShepherdTourFieldLocks();
  ensureShepherdModalOverlayHidden();
}

const MESSAGES_CONNECTIONS_SEARCH_OK = {
  text: 'OK',
  classes: 'shepherd-button-primary',
  action(this: ShepherdTour) {
    clearMessagesConnectionsSearchStepOverlay();
    dismissCurrentShepherdStep(this);
  },
};

const connectionsSearchStepButtons = [TOUR_BUTTONS.skip, MESSAGES_CONNECTIONS_SEARCH_OK];

/**
 * Messages → Connections (first tab): search-bar tip when inbox is empty or when connections exist.
 */
export function buildMessagesConnectionsTour(tour: ShepherdTour): void {
  registerInteractiveTourGuards(tour);
  registerTourHighlightedFieldLockGuards(
    tour,
    [...MESSAGES_CONNECTIONS_SEARCH_STEP_IDS],
    resolveMessagesConnectionsSearchTarget,
  );

  const attachSelector = '[data-tour="messages-connections-search"]';

  tour.on('show', (ev: { step?: { id?: string } }) => {
    const stepId = ev?.step?.id;
    if (
      stepId &&
      (MESSAGES_CONNECTIONS_SEARCH_STEP_IDS as readonly string[]).includes(stepId)
    ) {
      setMessagesConnectionsSearchInteractionActive(true);
      resetMessagesConnectionsSearchTargetPadding();
      return;
    }
    if (stepId) {
      setMessagesConnectionsSearchInteractionActive(false);
      resetMessagesConnectionsSearchTargetPadding();
    }
  });

  tour.on('hide', (ev: { step?: { id?: string } }) => {
    const stepId = ev?.step?.id;
    if (
      stepId &&
      (MESSAGES_CONNECTIONS_SEARCH_STEP_IDS as readonly string[]).includes(stepId)
    ) {
      clearMessagesConnectionsSearchStepOverlay();
    }
  });

  tour.addStep({
    id: MESSAGES_CONNECTIONS_INTRO_STEP_ID,
    title: 'Tip',
    text: withTourFieldLockTipMessage(
      'No conversations yet. Start connecting with golfers nearby.',
    ),
    attachTo: { element: attachSelector, on: 'bottom' },
    buttons: connectionsSearchStepButtons,
    ...SHEPHERD_TOUR_READONLY_HIGHLIGHT_STEP_OPTIONS,
    showOn: () => messagesConnectionsHasEmptyState(),
    beforeShowPromise() {
      return waitForElement(attachSelector, 15000).then(() => undefined);
    },
  });

  tour.addStep({
    id: MESSAGES_CONNECTIONS_SEARCH_LIST_STEP_ID,
    title: 'Tip',
    text: withTourFieldLockTipMessage('Start connecting with golfers nearby.'),
    attachTo: { element: attachSelector, on: 'bottom' },
    buttons: [TOUR_BUTTONS.skip, TOUR_BUTTONS.finish],
    ...SHEPHERD_TOUR_READONLY_HIGHLIGHT_STEP_OPTIONS,
    showOn: () => messagesConnectionsHasList(),
    beforeShowPromise() {
      return waitForElement(attachSelector, 15000).then(() => undefined);
    },
  });
}
